import { Library as LibraryIcon, Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useGenerations from "@/hooks/useGenerations";
import GenerationCard from "@/components/GenerationCard";
import { TikTokAccountCard } from "@/components/TikTokAccountCard";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function matches(gen, q) {
  if (!q) return true;
  const t = q.toLowerCase();
  const m = gen.metadata || {};
  const hay = [
    gen.prompt, gen.genre, gen.mood,
    m.key, String(m.bpm || ""), m.title, m.description,
    ...(m.tags || []),
  ].join(" ").toLowerCase();
  return hay.includes(t);
}

export default function Library() {
  const { refreshUser } = useAuth();
  const { generations, loading, refetch } = useGenerations();
  const [varLoading, setVarLoading] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");

  useEffect(() => { setQuery(searchParams.get("q") || ""); }, [searchParams]);
  useEffect(() => {
    const tt = searchParams.get("tiktok");
    if (!tt) return;
    if (tt === "connected") toast.success("TikTok account connected — you can now publish your vertical clips.");
    else {
      const reason = searchParams.get("reason") || "unknown";
      const hints = { access_denied: "Authorization was refused. In the TikTok Developer Portal (Sandbox), enable the Content Posting API with Direct Post, tick the video.publish scope and add your TikTok account to Target Users.", token_exchange: "Token exchange failed — check the redirect URI registered in the TikTok portal.", invalid_state: "Session expired — please retry." };
      toast.error(`TikTok connection failed (${reason}). ${hints[reason] || ""}`, { duration: 12000 });
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, [searchParams]);

  const filtered = useMemo(() => generations.filter((g) => matches(g, query)), [generations, query]);

  const updateQuery = (v) => {
    setQuery(v);
    if (v) setSearchParams({ q: v }); else setSearchParams({});
  };

  const onVariation = async (id) => {
    setVarLoading(id);
    try {
      await api.post(`/generations/${id}/variation`);
      toast.success("Variation started.");
      await refetch();
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setVarLoading(null);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/generations/${id}`);
      await refetch();
    } catch {
      toast.error("Could not delete generation.");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl overflow-x-hidden">
      <header className="mb-6">
        <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
          <LibraryIcon className="text-primary" /> Library
        </h1>
        <p className="text-muted-foreground mt-1">All your generated soundtracks, stems and variations.</p>
      </header>

      <TikTokAccountCard />

      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => updateQuery(e.target.value)}
          placeholder="Filter by key, BPM, tag, genre, mood…"
          className="pl-9 pr-9 bg-card border-border"
          data-testid="library-search"
        />
        {query && (
          <Button variant="ghost" size="icon" onClick={() => updateQuery("")} className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground" data-testid="clear-search">
            <X size={15} />
          </Button>
        )}
      </div>

      {query && !loading && (
        <div className="mb-4 text-sm text-muted-foreground" data-testid="filter-result-count">
          {filtered.length} track{filtered.length === 1 ? "" : "s"} matching <span className="font-mono text-secondary">{query}</span>
        </div>
      )}

      {loading ? (
        <div className="text-muted-foreground font-mono animate-pulse">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="empty-library">
          {generations.length === 0 ? "Nothing here yet. Head to the Studio to create music." : "No tracks match this filter."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((g) => (
            <GenerationCard key={g.id} gen={g} onVariation={onVariation} onDelete={onDelete} varLoading={varLoading === g.id} onSaved={refetch} />
          ))}
        </div>
      )}
    </div>
  );
}
